import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType, DokladyDokladDto } from '@gov-nx/api';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import {
	getKeys,
	PersonalDocumentType,
	propEqPartial,
} from '@gov-nx/core/types';
import type { Tree } from '@gov-nx/ui/types';
import {
	dataBoxParams,
	today,
	toStringDate,
	useAutocompleteDefinition,
	useDataBoxShape,
	useDateShape,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type {
	DataRequest,
	FormData,
	FormDataStep2,
	FormDataStep2Fields,
	FormDataStep3,
	FormDataStep4,
	Scope,
	ScopeHelperField,
} from './service.types';
import {
	ScopeDocuments,
	ScopeNonReferential,
	ScopeReferential,
} from './service.types';
import { useAuthorizationConsentYupSchema } from '@gov-nx/core/hooks';

const defaultValues = (): { [key in keyof Scope]: boolean } => ({
	Jmeno: false,
	Prijmeni: false,
	Pohlavi: false,
	RodinnyStavPartnerstvi: false,
	AdresaPobytu: false,
	DorucovaciAdresa: false,
	DatumNarozeni: false,
	MistoNarozeni: false,
	OmezeniSvepravnosti: false,
	DatumUmrti: false,
	MistoUmrti: false,
	Obcanstvi: false,
	DatovaSchranka: false,

	Telefon: false,
	Email: false,
	Certifikat: false,

	DokladOP: false,
	DokladPAS: false,
	DokladVS: false,
	DokladPS: false,
	DokladPPP: false,

	_referential: false,
	_nonReferential: false,
	_documents: false,

	vsechnyUdaje: false,
});

export interface FormInstanceProps {
	code: ServiceCode;
	isDisabled: boolean;
}

export function FormInstanceStep2({ code, isDisabled }: FormInstanceProps): {
	formDefinition: FormDefinition<FormDataStep2>;
	fields: FormDataStep2Fields;
} {
	const { getDatePickerShape } = useDateShape();
	const { t } = useLocale(code);

	const foDs = useAutocompleteDefinition({
		name: 'foDs',
		isDisabled: isDisabled,
		placeholder: 'Vyhledat datovou schránku',
		label: t('formular.pole.datova-schranka-fo'),
		isRequiredWhen: {
			fieldName: 'typOsoby',
			is: 'FO',
			errorMessage: t(
				'formular.validace.fyzicka-osoba-datova-schranka.povinny'
			),
		},
	});

	const poIco = useAutocompleteDefinition({
		name: 'poIco',
		isDisabled: isDisabled,
		label: t('formular.pole.ico'),
		isRequiredWhen: {
			fieldName: 'typOsoby',
			is: 'PO',
			errorMessage: t('formular.validace.pravnicka-osoba-ico.povinny'),
		},
	});

	const formSchema: yup.ObjectSchema<FormDataStep2> = yup
		.object({
			typOsoby: yup
				.string()
				.oneOf(['FO', 'PO'])
				.required(t('formular.validace.typ-osoby.povinny')),

			foDs: foDs.schema,
			foTypDokladu: yup
				.string()
				.oneOf([PersonalDocumentType.IDCard, PersonalDocumentType.passport])
				.optional()
				.when('typOsoby', {
					is: 'FO',
					then: (schema) =>
						schema.required(
							t('formular.validace.fyzicka-osoba-typ-dokladu.povinny')
						),
				}),
			foCisloDokladu: yup
				.number()
				.typeError(t('formular.validace.fyzicka-osoba-cislo-dokladu.povinny'))
				.when('typOsoby', {
					is: 'FO',
					then: (schema) =>
						schema.required(
							t('formular.validace.fyzicka-osoba-cislo-dokladu.povinny')
						),
				}),
			foDatumNarozeni: getDatePickerShape({
				isInvalidMessage: t(
					'formular.validace.fyzicka-osoba-datum-narozeni.povinny'
				),
			})
				.max(
					today(),
					t('formular.validace.fyzicka-osoba-datum-narozeni.maximum')
				)
				.when('typOsoby', {
					is: 'FO',
					then: (schema) =>
						schema.required(
							t('formular.validace.fyzicka-osoba-datum-narozeni.povinny')
						),
				}),

			poIco: poIco.schema,
		})
		.required() as yup.ObjectSchema<FormDataStep2>;

	const formMethods = useGovForm<FormDataStep2>({
		formSchema,
		defaultValues: {
			typOsoby: undefined,
			foDs: foDs.defaultValue,
			foTypDokladu: PersonalDocumentType.IDCard,
			foCisloDokladu: undefined,
			foDatumNarozeni: undefined,
			poIco: poIco.defaultValue,
		},
	});

	return {
		fields: { foDs: foDs.field },
		formDefinition: getFormDefinition({ formMethods, formSchema }),
	};
}

export function FormInstanceStep3(): FormDefinition<FormDataStep3> {
	const fieldsSchema = getKeys(defaultValues()).reduce(
		(all, key) => ({ ...all, [key]: yup.boolean().optional() }),
		{} as { [key in keyof Scope]: yup.BooleanSchema }
	);
	const formSchema = yup
		.object({
			opakovanePoskytnuti: yup.boolean().optional(),
			udaj: yup.object(fieldsSchema).optional(),
		})
		.required();

	const formMethods = useGovForm<FormDataStep3>({
		formSchema,
		defaultValues: {
			opakovanePoskytnuti: false,
			udaj: defaultValues(),
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

interface FormInstanceStep4Props {
	dataBoxTypes: DatovkaApiType['typSchranky'][];
	isLowAuthorization: boolean;
}

export function FormInstanceStep4({
	dataBoxTypes,
	isLowAuthorization,
}: FormInstanceStep4Props): FormDefinition<FormDataStep4> {
	const { getDataBoxShape, getDataBoxDefaultValues } = useDataBoxShape({
		types: dataBoxTypes,
	});

	const dataBoxShape = getDataBoxShape({ isRequired: true });

	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: isLowAuthorization,
	});

	const formSchema = yup
		.object({ ...dataBoxShape })
		.shape({ __souhlas: consentSchema })
		.required();

	const formMethods = useGovForm<FormDataStep4>({
		formSchema,
		defaultValues: getDataBoxDefaultValues(),
	});

	return getFormDefinition({ formMethods, formSchema });
}

const filterChecked =
	(formData: FormData) =>
	(
		scope:
			| typeof ScopeReferential
			| typeof ScopeNonReferential
			| typeof ScopeDocuments
	) =>
		getKeys(scope).filter((key) => formData.udaj?.[key]);

export const scope =
	(formData: FormData) =>
	(
		name: keyof typeof ScopeHelperField
	): Array<
		| keyof typeof ScopeReferential
		| keyof typeof ScopeNonReferential
		| keyof typeof ScopeDocuments
	> => {
		const inScope = filterChecked(formData);
		switch (name) {
			case '_documents':
				return inScope(ScopeDocuments);
			case '_nonReferential':
				return inScope(ScopeNonReferential);
			case '_referential':
				return inScope(ScopeReferential);
		}
	};

export const scopeWithLabels =
	(tree: Tree) =>
	(formData: FormData) =>
	(
		name: keyof typeof ScopeHelperField
	): Array<{
		name:
			| keyof typeof ScopeReferential
			| keyof typeof ScopeNonReferential
			| keyof typeof ScopeDocuments;
		label?: string;
	}> => {
		const nameEq = propEqPartial('name');
		const groupField = tree.children.find(nameEq(`udaj.${name}`));
		if (!groupField) {
			return [];
		}

		return scope(formData)(name).map((key) => ({
			name: key,
			label: groupField.children.find(nameEq(`udaj.${key}`))?.label,
		}));
	};

export const prepareSubmitData =
	(document?: DokladyDokladDto, ds?: DatovkaApiType) =>
	(data: FormData): DataRequest => {
		const inScope = scope(data);

		return {
			params: dataBoxParams(data),
			body: {
				kodPodani: 'ZR10_POSKYTNUTI',
				...(data.typOsoby === 'FO'
					? {
							typOsoby: 'FO',
							foTypDokladu: data.foTypDokladu,
							foCisloDokladu: data.foCisloDokladu,
							foDatumNarozeni: data.foDatumNarozeni
								? toStringDate(data.foDatumNarozeni)
								: undefined,
							foJmeno: ds?.jmeno,
							foPrijmeni: ds?.prijmeni,
							foAdresa: ds?.adresa,
							foDs: ds?.datovaSchrankaId,
						}
					: {
							typOsoby: 'PO',
							poIco: data.poIco?.selected?.ico,
							poNazev: data.poIco?.selected?.nazevSpolecnosti,
							poDs: data.poIco?.selected?.datovaSchrankaId,
							poNazevMesta: data.poIco?.selected?.sidloSpolecnosti?.nazevMesta,
							poPsc: data.poIco?.selected?.sidloSpolecnosti?.psc,
							poNazevCastiObce:
								data.poIco?.selected?.sidloSpolecnosti?.nazevCastiObce,
							poCisloPopisne:
								data.poIco?.selected?.sidloSpolecnosti?.cisloPopisne,
							poNazevUlice: data.poIco?.selected?.sidloSpolecnosti?.nazevUlice,
							poCisloOrientacni:
								data.poIco?.selected?.sidloSpolecnosti?.cisloOrientacni,
						}),

				opakovanePoskytnuti: data.opakovanePoskytnuti,
				vsechnyUdaje: data.udaj?.vsechnyUdaje,
				udaj: !data.udaj?.vsechnyUdaje
					? [
							...inScope('_referential'),
							...inScope('_nonReferential'),
							...inScope('_documents'),
						]
					: [],

				typCisloID: `${
					document?.druhDokladu === 'ID' ? 'Občanský průkaz' : 'Cestovní pas'
				}: ${document?.cisloDokladu}`,
			},
		};
	};

export const useFormTree = (): Tree => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);
	return {
		name: 'udaj.vsechnyUdaje',
		label: t('vyber-udaju.stitky.vsechny-udaje'),
		isOpen: false,
		isToggleable: true,
		children: [
			{
				name: 'udaj._referential',
				label: t('vyber-udaju.stitky.referencni-udaje'),
				isOpen: false,
				isToggleable: true,
				children: [
					{ name: 'udaj.Jmeno', label: t('vyber-udaju.stitky.jmeno') },
					{ name: 'udaj.Prijmeni', label: t('vyber-udaju.stitky.prijmeni') },
					{ name: 'udaj.Pohlavi', label: t('vyber-udaju.stitky.pohlavi') },
					{
						name: 'udaj.RodinnyStavPartnerstvi',
						label: t('vyber-udaju.stitky.rodinny-stav-partnerstvi'),
					},
					{
						name: 'udaj.AdresaPobytu',
						label: t('vyber-udaju.stitky.adresa-pobytu'),
					},
					{
						name: 'udaj.DorucovaciAdresa',
						label: t('vyber-udaju.stitky.dorucovaci-adresa'),
					},
					{
						name: 'udaj.DatumNarozeni',
						label: t('vyber-udaju.stitky.datum-narozeni'),
					},
					{
						name: 'udaj.MistoNarozeni',
						label: t('vyber-udaju.stitky.misto-narozeni'),
					},
					{
						name: 'udaj.OmezeniSvepravnosti',
						label: t('vyber-udaju.stitky.omezeni-svepravnosti'),
					},
					{
						name: 'udaj.DatumUmrti',
						label: t('vyber-udaju.stitky.datum-umrti'),
					},
					{
						name: 'udaj.MistoUmrti',
						label: t('vyber-udaju.stitky.misto-umrti'),
					},
					{
						name: 'udaj.Obcanstvi',
						label: t('vyber-udaju.stitky.obcanstvi'),
					},
					{
						name: 'udaj.DatovaSchranka',
						label: t('vyber-udaju.stitky.datova-schranka'),
					},
				],
			},
			{
				name: 'udaj._nonReferential',
				label: t('vyber-udaju.stitky.nereferencni-udaje'),
				isOpen: false,
				isToggleable: true,
				children: [
					{ name: 'udaj.Telefon', label: t('vyber-udaju.stitky.telefon') },
					{ name: 'udaj.Email', label: t('vyber-udaju.stitky.email') },
					{
						name: 'udaj.Certifikat',
						label: t('vyber-udaju.stitky.certifikat'),
					},
				],
			},
			{
				name: 'udaj._documents',
				label: t('vyber-udaju.stitky.doklady'),
				isOpen: false,
				isToggleable: true,
				children: [
					{ name: 'udaj.DokladOP', label: t('vyber-udaju.stitky.doklad-OP') },
					{
						name: 'udaj.DokladPAS',
						label: t('vyber-udaju.stitky.doklad-PAS'),
					},
					{ name: 'udaj.DokladVS', label: t('vyber-udaju.stitky.doklad-VS') },
					{ name: 'udaj.DokladPS', label: t('vyber-udaju.stitky.doklad-PS') },
					{
						name: 'udaj.DokladPPP',
						label: t('vyber-udaju.stitky.doklad-PPP'),
					},
				],
			},
		],
	};
};
